import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { Comment } from "../../types";
import {
  getMapComments,
  addComment,
  deleteComment,
} from "../../services/socialService";
import { useAuth } from "../../contexts/AuthContext";
import { Send, Trash2, User } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const CommentsContainer = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  overflow: hidden;
`;

const CommentsHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-color);
`;

const CommentsList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CommentItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  gap: 1rem;

  &:last-child {
    border-bottom: none;
  }
`;

const CommentAvatar = styled.div<{ avatar?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) =>
    props.avatar ? `url(${props.avatar})` : "var(--primary-color)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const CommentContent = styled.div`
  flex: 1;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: var(--text-color);
`;

const CommentTime = styled.span`
  font-size: 0.8rem;
  color: var(--text-secondary-color);
`;

const CommentText = styled.p`
  margin: 0;
  color: var(--text-color);
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: var(--error-bg);
  }
`;

const AddCommentForm = styled.form`
  padding: 1.5rem 1rem 1rem 1rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: var(--surface-color);
  @media (max-width: 600px) {
    padding: 1rem 0.5rem;
  }
`;

const CommentInput = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-size: 15px;
  resize: vertical;
  min-height: 40px;
  max-height: 120px;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;
  background: var(--background-color);
  color: var(--text-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  @media (max-width: 600px) {
    min-height: 36px;
    font-size: 14px;
  }
`;

const SendButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.2rem;
  min-width: 44px;
  min-height: 44px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 1rem;
  box-sizing: border-box;
  align-self: flex-end;
  margin-left: auto;

  &:hover {
    background: var(--primary-color);
    opacity: 0.8;
  }

  &:disabled {
    background: var(--secondary-color);
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    display: block;
  }
  @media (max-width: 600px) {
    width: auto;
    min-width: 0;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary-color);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary-color);
`;

interface CommentsSectionProps {
  mapId: string;
  onCommentAdded?: () => void;
  onCommentsCountChange?: (count: number) => void;
}

const CommentsSection: React.FC<CommentsSectionProps> = ({
  mapId,
  onCommentAdded,
  onCommentsCountChange,
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const mapComments = await getMapComments(mapId);
      setComments(mapComments);

      if (onCommentsCountChange) {
        onCommentsCountChange(mapComments.length);
      }
    } catch (err) {
      console.error("Error loading comments:", err);
    } finally {
      setLoading(false);
    }
  }, [mapId, onCommentsCountChange]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !newComment.trim()) {
      return;
    }

    try {
      setSubmitting(true);

      await addComment(
        mapId,
        user.id,
        user.displayName,
        user.avatar,
        newComment.trim()
      );

      setNewComment("");

      // Add a small delay to ensure Firestore has processed the write
      await new Promise((resolve) => setTimeout(resolve, 1000));

      await loadComments(); // Reload comments to get the new one

      if (onCommentAdded) {
        onCommentAdded();
      }
    } catch (err) {
      console.error("Error in comment submission:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!user) return;

    try {
      await deleteComment(commentId, mapId);
      await loadComments(); // Reload comments
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  if (loading) {
    return (
      <CommentsContainer>
        <CommentsHeader>
          <User size={20} />
          Comments
        </CommentsHeader>
        <LoadingSpinner>Loading comments...</LoadingSpinner>
      </CommentsContainer>
    );
  }

  return (
    <CommentsContainer>
      <CommentsHeader>
        <User size={20} />
        Comments ({comments.length})
      </CommentsHeader>

      <CommentsList>
        {comments.length === 0 ? (
          <EmptyState>
            <p>No comments yet. Be the first to comment!</p>
          </EmptyState>
        ) : (
          comments.map((comment) => (
            <CommentItem key={comment.id}>
              <CommentAvatar avatar={comment.userAvatar}>
                {!comment.userAvatar &&
                  comment.userDisplayName.charAt(0).toUpperCase()}
              </CommentAvatar>
              <CommentContent>
                <CommentHeader>
                  <CommentAuthor>{comment.userDisplayName}</CommentAuthor>
                  <CommentTime>
                    {formatDistanceToNow(new Date(comment.createdAt), {
                      addSuffix: true,
                    })}
                  </CommentTime>
                  {user && comment.userId === user.id && (
                    <DeleteButton
                      onClick={() => handleDeleteComment(comment.id)}
                      title="Delete comment"
                    >
                      <Trash2 size={14} />
                    </DeleteButton>
                  )}
                </CommentHeader>
                <CommentText>{comment.content}</CommentText>
              </CommentContent>
            </CommentItem>
          ))
        )}
      </CommentsList>

      {user && (
        <AddCommentForm onSubmit={handleSubmitComment}>
          <CommentInput
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            disabled={submitting}
          />
          <SendButton type="submit" disabled={submitting || !newComment.trim()}>
            <Send size={18} />
            {submitting ? "Sending..." : "Send"}
          </SendButton>
        </AddCommentForm>
      )}
    </CommentsContainer>
  );
};

export default CommentsSection;
